<script lang="ts">
  // Embeddable public comment feed for a "subject" (an activity or a calendar day). Unlike a DM
  // conversation, there's no picker, no participants, and no "mine vs theirs" bubble alignment --
  // every comment shows its own author, and anyone logged in as the subject athlete (the athlete
  // themself, or any of their coaches) can read and post here. Never auto-created: posting the
  // first comment is what brings the thread into existence.
  //
  // By default this panel flows inline in whatever scrollable dialog hosts it, with a "Load
  // older" button (CommentList) for pagination and a "Jump to latest" button instead of an
  // internal scroll region. Pass fillHeight for a host that gives this panel a real bounded-height
  // slot to fill (the centralized Messages page) -- comments scroll in their own region and the
  // composer is pinned outside it, always visible. Same state/handlers either way, just arranged
  // differently in the template below.
  import { onDestroy, tick } from 'svelte';
  import {
    deleteComment as apiDeleteComment,
    editComment as apiEditComment,
    forwardCommentToConversation,
    forwardCommentToThread,
    getForwardTargets,
    pinComment as apiPinComment,
    toggleCommentReaction,
    unpinComment as apiUnpinComment,
  } from './api';
  import type { Comment, CommentThread, ForwardTargets } from './api';
  import type { ExerciseReference, MessagingUser } from '../Messaging/api';
  import CommentList from './CommentList.svelte';
  import ThreadComposer from '../Messaging/ThreadComposer.svelte';
  import PinnedList from '../Messaging/PinnedList.svelte';
  import ForwardPicker from './ForwardPicker.svelte';
  import ErrorBanner from '../Messaging/ErrorBanner.svelte';
  import { Button } from '$lib/components/ui/button';
  import { jumpToAndHighlight } from '@/Util/utils';

  const PAGE_SIZE = 30;

  // $before is the cursor for an older page; omitted (undefined) for the initial/most-recent load.
  export let load: (before?: string) => Promise<{ thread: CommentThread | null; comments: Comment[] }>;
  export let send: (text: string, files: File[], references: ExerciseReference[], replyToId?: string) => Promise<{ thread: CommentThread; comment: Comment }>;
  export let markRead: () => Promise<void>;
  export let fillHeight: boolean = false;
  /**
   * Whether to jump to the newest comment once the thread has loaded. On a page that is nothing but
   * the thread that's what you want; embedded partway down something longer it isn't, because
   * scrollIntoView() scrolls every scrollable ancestor and the host opens at its own bottom.
   */
  export let scrollToLatestOnLoad: boolean = true;
  // Called every time this thread gets marked read (on open, and on a live incoming comment while
  // already open) -- lets a parent maintaining its own thread list (with its own unread_count)
  // clear that badge locally instead of waiting on an unrelated refetch.
  export let onRead: () => void = () => {};

  //@ts-ignore
  const Xert = window.Xert;
  const currentUserId: number = Xert?._userId;

  let thread: CommentThread | null = null;
  let comments: Comment[] = [];
  let loading = true;
  let loadingMore = false;
  let hasMore = false;
  let sending = false;
  let error: string | null = null;
  // Sentinel after the composer -- the true bottom of the panel -- rather than one at the end of
  // the comment list, so "Jump to latest"/atBottom account for the composer being visible too.
  // Only used in the default (non-fillHeight) layout -- see the atBottom section below for why
  // fillHeight computes it differently.
  let commentsEndEl: HTMLElement | undefined;
  let panelEndEl: HTMLElement | undefined;
  // The id of the first still-unread comment at the moment this panel opened -- computed once in
  // init() (before marking read) and never recomputed, so the "N unread" divider stays put where
  // you left off instead of chasing you down the list as more get marked read.
  let unreadDividerId: string | null = null;
  // In fillHeight mode the comments themselves are the scroll container (the composer sits
  // outside it, always visible) -- bound so atBottom can be computed directly from real scroll
  // metrics instead of a sentinel.
  let commentsScrollEl: HTMLElement | undefined;
  let atBottom = true;
  let observer: IntersectionObserver | undefined;

  let replyTo: Comment | null = null;
  let editing: Comment | null = null;
  let forwarding: Comment | null = null;
  let showForwardPicker = false;
  let forwardTargets: ForwardTargets | null = null;
  let forwardTargetsLoading = false;

  // The composer only needs a small resolved shape, not the full Comment -- see
  // Messaging/ConversationPane.svelte for why these are wrapped in functions rather than
  // `replyTo && {...}` inline (TS control-flow narrowing false positive otherwise).
  function toReplyPreview(c: Comment | null) {
    return c ? { id: c.id, label: c.author ? c.author.first_name : 'them', preview: c.body ?? '[attachment]' } : null;
  }
  function toEditPreview(c: Comment | null) {
    return c ? { id: c.id, body: c.body ?? '' } : null;
  }
  $: replyToForComposer = toReplyPreview(replyTo);
  $: editingForComposer = toEditPreview(editing);

  // panelEndEl is a stable sentinel (a static element after the composer, not recreated as
  // comments change), so the observer only needs to be attached once. fillHeight mode never sets
  // this up at all -- see handleCommentsScroll below, which computes atBottom directly instead.
  $: if (!fillHeight && commentsEndEl && !observer) {
    observer = new IntersectionObserver(([entry]) => (atBottom = entry.isIntersecting), { threshold: 0 });
    observer.observe(commentsEndEl);
  }
  onDestroy(() => observer?.disconnect());

  // A zero-height sentinel + IntersectionObserver (the mechanism above) is unreliable once it's
  // actually observing within a small internal scroll container rather than an ancestor dialog --
  // fillHeight mode owns a real scrollable element (commentsScrollEl), so it can just check real
  // scroll metrics directly instead.
  const NEAR_BOTTOM_PX = 24;
  function handleCommentsScroll() {
    if (!commentsScrollEl) return;
    const { scrollTop, scrollHeight, clientHeight } = commentsScrollEl;
    atBottom = scrollHeight - scrollTop - clientHeight < NEAR_BOTTOM_PX;
  }
  // Scrolling isn't the only thing that can change what's "at the bottom" -- new comments (or the
  // initial load) change scrollHeight without necessarily firing a scroll event, so recompute
  // after the DOM actually reflects the new comment list.
  $: if (fillHeight && commentsScrollEl && comments) {
    tick().then(handleCommentsScroll);
  }

  async function scrollToBottom() {
    await tick();
    panelEndEl?.scrollIntoView({ block: 'end' });
  }

  // Always keep the feed in chronological order -- comments can arrive out of send order (a
  // realtime event racing a locally-posted one, or two people posting at nearly the same time),
  // so appending blindly isn't reliable. Dedupes by id too, in case the same comment ever arrives
  // both as a direct response and a realtime echo.
  function sortByDate(list: Comment[]): Comment[] {
    return [...list].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }

  function addComment(comment: Comment) {
    if (comments.some((c) => c.id === comment.id)) return;
    comments = sortByDate([...comments, comment]);
  }

  // Notifies both the parent's own list (onRead, for whichever embed this panel is in -- the
  // centralized Messages page, ExerciseDialog, CommentsButton's dialog, ...) and the navbar
  // Messages icon (Xert.markMessageNotificationsRead(), added by an earlier change) that this
  // thread just got marked read -- centralized here so every current and future caller gets it
  // for free instead of each one having to remember to wire it up itself.
  function notifyRead() {
    if (thread == null) return;
    onRead();
    //@ts-ignore
    window.Xert?.markMessageNotificationsRead?.({ threadId: thread.id });
  }

  const onComment = (event: { thread_id: string; comment: Comment }) => {
    if (thread == null || event.thread_id !== thread.id) return;
    addComment(event.comment);
    // Only auto-scroll if the user is already at the bottom -- otherwise let them keep
    // reading history undisturbed; the "Jump to latest" button covers that case instead.
    if (atBottom) scrollToBottom();
    if (event.comment.author?.id !== currentUserId) {
      void markRead();
      notifyRead();
    }
  };

  const onMessageRead = (event: { thread_id: string; read_by: number }) => {
    if (thread == null || event.thread_id !== thread.id) return;
    comments = comments.map((c) => {
      if (c.author?.id !== currentUserId || (c.read_by ?? []).some((r) => r.user_id === event.read_by)) return c;
      return { ...c, read_by: [...(c.read_by ?? []), { user_id: event.read_by, at: new Date().toISOString() }] };
    });
  };

  // Shared by the comment_edited/deleted/reacted/pinned/whatsapp_synced events -- all five carry
  // the same {thread_id, comment} shape (the service methods that trigger them return the full
  // updated comment, same as posting one), so one handler replaces the matching row in place.
  const onCommentUpdated = (event: { thread_id: string; comment: Comment }) => {
    if (thread == null || event.thread_id !== thread.id) return;
    comments = comments.map((c) => (c.id === event.comment.id ? event.comment : c));
  };

  const channel = Xert?._userChannel;
  if (channel) {
    channel.bind('comment', onComment);
    channel.bind('message_read', onMessageRead);
    channel.bind('comment_edited', onCommentUpdated);
    channel.bind('comment_deleted', onCommentUpdated);
    channel.bind('comment_reacted', onCommentUpdated);
    channel.bind('comment_pinned', onCommentUpdated);
    channel.bind('comment_whatsapp_synced', onCommentUpdated);
  }
  onDestroy(() => {
    if (channel) {
      channel.unbind('comment', onComment);
      channel.unbind('message_read', onMessageRead);
      channel.unbind('comment_edited', onCommentUpdated);
      channel.unbind('comment_deleted', onCommentUpdated);
      channel.unbind('comment_reacted', onCommentUpdated);
      channel.unbind('comment_pinned', onCommentUpdated);
      channel.unbind('comment_whatsapp_synced', onCommentUpdated);
    }
  });

  // Same dedup as Messaging's ConversationPane: for each thread participant other than
  // me, find their most recently read comment and only attach their avatar there.
  $: readersByCommentId = computeReadersByComment(comments, thread, currentUserId);

  function computeReadersByComment(list: Comment[], commentThread: CommentThread | null, selfId: number) {
    const map = new Map<string, MessagingUser[]>();
    if (!commentThread) return map;

    const latestByReader = new Map<number, { commentId: string; at: number }>();
    for (const c of list) {
      for (const r of c.read_by ?? []) {
        if (r.user_id === selfId) continue;
        const at = new Date(r.at).getTime();
        const existing = latestByReader.get(r.user_id);
        if (!existing || at >= existing.at) {
          latestByReader.set(r.user_id, { commentId: c.id, at });
        }
      }
    }

    for (const [readerId, { commentId }] of latestByReader) {
      const user = commentThread.participants.find((p) => p.id === readerId);
      if (!user) continue;
      map.set(commentId, [...(map.get(commentId) ?? []), user]);
    }

    return map;
  }

  function readersFor(commentId: string): MessagingUser[] {
    return readersByCommentId.get(commentId) ?? [];
  }

  $: pinnedComments = comments
    .filter((c) => c.pinned_at && !c.deleted_at)
    .sort((a, b) => new Date(b.pinned_at ?? 0).getTime() - new Date(a.pinned_at ?? 0).getTime());

  // PinnedList only needs a small resolved shape ({id, label, preview}) -- unlike Messaging's
  // ChatMessage, a Comment already carries a resolved `author` object, so no participant lookup
  // is needed here.
  $: pinnedForList = pinnedComments.map((c) => ({
    id: c.id,
    label: c.author ? `${c.author.first_name} ${c.author.last_name}` : 'Unknown',
    preview: c.body ?? '[attachment]',
  }));

  function handleUnpinById(commentId: string) {
    const comment = comments.find((c) => c.id === commentId);
    if (comment) handleUnpin(comment);
  }

  async function init() {
    loading = true;
    try {
      const result = await load();
      thread = result.thread;
      comments = sortByDate(result.comments);
      hasMore = result.comments.length >= PAGE_SIZE;
      // Snapshot the unread boundary before marking read -- markRead() never mutates `comments`
      // locally (it's a fire-and-forget POST), so this is safe to compute right here.
      const firstUnread = comments.find((c) => c.author?.id !== currentUserId && !(c.read_by ?? []).some((r) => r.user_id === currentUserId));
      unreadDividerId = firstUnread?.id ?? null;
      // Flip loading before scrolling -- the panel (and the panelEndEl sentinel it scrolls to)
      // only renders once `!loading`, so calling scrollToBottom() any earlier finds panelEndEl
      // still undefined and silently no-ops, leaving the panel at the top instead of jumping to
      // the latest comment.
      loading = false;
      if (scrollToLatestOnLoad) await scrollToBottom();
      if (thread != null) {
        void markRead();
        notifyRead();
      }
    } catch (e) {
      error = 'Unable to load comments right now.';
      console.log(e);
      loading = false;
    }
  }
  void init();

  async function loadMore() {
    if (loadingMore || comments.length === 0) return;
    loadingMore = true;
    try {
      const oldest = comments[0];
      const result = await load(oldest.created_at);
      const older = result.comments.filter((c) => !comments.some((existing) => existing.id === c.id));
      comments = sortByDate([...older, ...comments]);
      hasMore = result.comments.length >= PAGE_SIZE;
    } catch (e) {
      error = 'Unable to load older comments.';
      console.log(e);
    } finally {
      loadingMore = false;
    }
  }

  async function onSend(text: string, files: File[], references: ExerciseReference[], replyToId?: string) {
    if (sending) return;
    sending = true;
    try {
      const result = await send(text, files, references, replyToId);
      thread = result.thread;
      addComment(result.comment);
      await scrollToBottom();
    } catch (e) {
      error = 'Comment failed to post.';
      console.log(e);
    } finally {
      sending = false;
    }
  }

  function handleReply(comment: Comment) {
    editing = null;
    replyTo = comment;
  }

  function handleEdit(comment: Comment) {
    replyTo = null;
    editing = comment;
  }

  function handleCancelEdit() {
    editing = null;
  }

  async function handleEditSubmit(id: string, body: string) {
    try {
      const updated = await apiEditComment(id, body);
      comments = comments.map((c) => (c.id === id ? updated : c));
    } catch (e) {
      error = 'Unable to save that edit.';
      console.log(e);
    }
  }

  async function handleDelete(comment: Comment) {
    try {
      await apiDeleteComment(comment.id);
      comments = comments.map((c) => (c.id === comment.id ? { ...c, deleted_at: new Date().toISOString() } : c));
    } catch (e) {
      error = 'Unable to delete that comment.';
      console.log(e);
    }
  }

  async function handleReact(comment: Comment, emoji: string) {
    try {
      const updated = await toggleCommentReaction(comment.id, emoji);
      comments = comments.map((c) => (c.id === comment.id ? updated : c));
    } catch (e) {
      error = 'Unable to react to that comment.';
      console.log(e);
    }
  }

  async function handlePin(comment: Comment) {
    try {
      const updated = await apiPinComment(comment.id);
      comments = comments.map((c) => (c.id === comment.id ? updated : c));
    } catch (e) {
      error = 'Unable to pin that comment.';
      console.log(e);
    }
  }

  async function handleUnpin(comment: Comment) {
    try {
      const updated = await apiUnpinComment(comment.id);
      comments = comments.map((c) => (c.id === comment.id ? updated : c));
    } catch (e) {
      error = 'Unable to unpin that comment.';
      console.log(e);
    }
  }

  async function handleForward(comment: Comment) {
    if (thread == null) return;
    forwarding = comment;
    showForwardPicker = true;
    forwardTargets = null;
    forwardTargetsLoading = true;
    try {
      forwardTargets = await getForwardTargets(thread.id);
    } catch (e) {
      error = 'Unable to load forward destinations.';
      console.log(e);
    } finally {
      forwardTargetsLoading = false;
    }
  }

  async function handleForwardPickConversation(conversationId: string) {
    if (!forwarding) return;
    try {
      await forwardCommentToConversation(forwarding.id, conversationId);
    } catch (e) {
      error = 'Unable to forward that comment.';
      console.log(e);
    } finally {
      forwarding = null;
    }
  }

  async function handleForwardPickThread(targetThreadId: string) {
    if (!forwarding) return;
    try {
      const forwarded = await forwardCommentToThread(forwarding.id, targetThreadId);
      if (thread && targetThreadId === thread.id) {
        addComment(forwarded);
        await scrollToBottom();
      }
    } catch (e) {
      error = 'Unable to forward that comment.';
      console.log(e);
    } finally {
      forwarding = null;
    }
  }

  function handleJumpTo(commentId: string) {
    jumpToAndHighlight(`comment-${commentId}`);
  }
</script>

{#if fillHeight}
  <div class="no-twp flex h-full min-h-0 w-full flex-col">
    {#if error}
      <ErrorBanner message={error} onDismiss={() => (error = null)} />
    {/if}
    {#if !loading}
      <PinnedList pinned={pinnedForList} noun="comment" onUnpin={handleUnpinById} onJumpTo={handleJumpTo} />
      <div class="relative min-h-0 flex-1">
        <div class="h-full overflow-y-auto" bind:this={commentsScrollEl} on:scroll={handleCommentsScroll}>
          <CommentList
            {comments}
            readersByComment={readersFor}
            {currentUserId}
            {unreadDividerId}
            {hasMore}
            {loadingMore}
            onLoadMore={loadMore}
            onReply={handleReply}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReact={handleReact}
            onPin={handlePin}
            onUnpin={handleUnpin}
            onForward={handleForward}
            onJumpTo={handleJumpTo}
          />
          <div bind:this={panelEndEl}></div>
        </div>
        {#if !atBottom && comments.length > 0}
          <div class="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex justify-center">
            <Button variant="secondary" size="sm" class="pointer-events-auto border-0 shadow-md" on:click={scrollToBottom}>
              <i class="fa-light fa-arrow-down mr-1"></i> Jump to latest
            </Button>
          </div>
        {/if}
      </div>
      <ThreadComposer
        noun="comment"
        {sending}
        onSend={onSend}
        onError={(message) => (error = message)}
        replyTo={replyToForComposer}
        onCancelReply={() => (replyTo = null)}
        editing={editingForComposer}
        onCancelEdit={handleCancelEdit}
        onEdit={handleEditSubmit}
      />
    {/if}
  </div>
{:else}
  <div class="no-twp flex w-full flex-col rounded-xl border border-border bg-card">
    {#if error}
      <ErrorBanner message={error} onDismiss={() => (error = null)} />
    {/if}
    {#if !loading}
      <PinnedList pinned={pinnedForList} noun="comment" onUnpin={handleUnpinById} onJumpTo={handleJumpTo} />
      <CommentList
        {comments}
        readersByComment={readersFor}
        {currentUserId}
        {unreadDividerId}
        {hasMore}
        {loadingMore}
        onLoadMore={loadMore}
        onReply={handleReply}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onReact={handleReact}
        onPin={handlePin}
        onUnpin={handleUnpin}
        onForward={handleForward}
        onJumpTo={handleJumpTo}
      />
      <div bind:this={commentsEndEl}></div>
      <ThreadComposer
        noun="comment"
        {sending}
        onSend={onSend}
        onError={(message) => (error = message)}
        replyTo={replyToForComposer}
        onCancelReply={() => (replyTo = null)}
        editing={editingForComposer}
        onCancelEdit={handleCancelEdit}
        onEdit={handleEditSubmit}
      />
      <div bind:this={panelEndEl}></div>
      {#if !atBottom && comments.length > 0}
        <div class="sticky bottom-1 z-10 flex justify-center py-1">
          <Button variant="secondary" size="sm" class="border-0 shadow-md" on:click={scrollToBottom}>
            <i class="fa-light fa-arrow-down mr-1"></i> Jump to latest
          </Button>
        </div>
      {/if}
    {/if}
  </div>
{/if}

<ForwardPicker
  bind:open={showForwardPicker}
  targets={forwardTargets}
  loading={forwardTargetsLoading}
  onPickConversation={handleForwardPickConversation}
  onPickThread={handleForwardPickThread}
/>
